import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/lib/images";
import { Countdown } from "./Countdown";
import { scrollToId } from "./Reveal";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: 1.1 + i * 0.15 } }),
};

const HEADLINE = ["Master the", "Art of the", "Crown."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen grain overflow-hidden bg-wine-deep pt-28 pb-16"
    >
      {/* radial glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-gold/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 h-[400px] w-[400px] rounded-full bg-wine/40 blur-[120px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-11rem)]">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
              Beauti N Braid Academy Presents
            </span>
          </motion.div>

          <h1 className="font-display font-light text-cream text-6xl sm:text-7xl lg:text-8xl leading-[0.92]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line.includes("Crown") ? (
                    <span className="italic text-gold">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-8 max-w-lg text-cream/70 text-base sm:text-lg leading-relaxed"
          >
            Introducing <span className="text-cream font-semibold">Crowned™ Pro — Level 1</span>. An
            elite braiding certification for artists ready to turn craft into a crown. This is more
            than training. This is transformation.
          </motion.p>

          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-enroll-btn"
              onClick={() => scrollToId("enroll")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-wine-deep hover:bg-cream transition-colors duration-300"
            >
              Reserve Your Seat
              <ArrowDown className="h-4 w-4 rotate-[-135deg] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-curriculum-btn"
              onClick={() => scrollToId("curriculum")}
              className="inline-flex items-center justify-center rounded-full border border-cream/25 px-8 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Explore the Program
            </button>
          </motion.div>

          <motion.div custom={2} variants={fadeUp} initial="hidden" animate="show" className="mt-14">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/50 mb-4">
              Doors open · September 15, 2026
            </div>
            <Countdown />
          </motion.div>
        </div>

        {/* Right: arched parallax image */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative arch overflow-hidden border border-gold/30 aspect-[3/4] max-w-md mx-auto">
            <motion.img
              src={IMAGES.heroPortrait}
              alt="Elegant braided crown"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-wine-deep/70 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-cream text-wine-deep rounded-2xl px-6 py-4 shadow-2xl">
            <div className="font-display text-3xl leading-none">Level 1</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-wine/60 mt-1">
              Crowned™ Pro
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
